import { useState, useEffect, useRef } from "react";
import { loadSquareSDK, SQUARE_CONFIG } from "./squareSdk";

// Generic "tokenize a card" form — reused by the signup page (new card) and
// the account page (replace card). Just tokenizes; the caller decides what
// to do with the token (create subscription, or call subscription-account
// with action: "update-card").
export default function SquareCardForm({ onTokenize, loading, error, buttonLabel }) {
  const cardRef = useRef(null);
  const mountedRef = useRef(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const containerId = useRef(`card-container-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    let card;
    loadSquareSDK()
      .then(async (Square) => {
        if (!mountedRef.current) return;
        const payments = Square.payments(SQUARE_CONFIG.appId, SQUARE_CONFIG.locationId);
        card = await payments.card({
          style: {
            ".input-container": { borderColor: "#C8D5C0", borderRadius: "0px" },
            ".input-container.is-focus": { borderColor: "#8C9E82" },
            ".input-container.is-error": { borderColor: "#9B4444" },
            input: { color: "#1E1C18" },
          },
        });
        if (!mountedRef.current) return;
        if (!document.getElementById(containerId.current)) {
          throw new Error("card container not found — component unmounted during initialization");
        }
        await card.attach(`#${containerId.current}`);
        if (!mountedRef.current) return;
        cardRef.current = card;
        setSdkReady(true);
      })
      .catch((err) => {
        console.error("Square SDK error:", err.message || err);
        if (mountedRef.current) setSdkError("Payment form failed to load. Please refresh and try again.");
      });

    return () => {
      if (card) card.destroy().catch(() => {});
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!cardRef.current) return;
    const result = await cardRef.current.tokenize();
    if (result.status === "OK") {
      onTokenize(result.token);
    } else {
      const msg = result.errors?.map((e) => e.message).join(", ") || "Card declined. Please try again.";
      onTokenize(null, msg);
    }
  };

  return (
    <div>
      <div id={containerId.current} style={{ minHeight: 90, background: "#F2EDE4", border: "1px solid #C8D5C0", padding: 14, marginBottom: 4, display: !sdkReady && !sdkError ? "flex" : "block", alignItems: "center", justifyContent: "center", color: "#8C9E82", fontSize: 12 }}>
        {!sdkReady && !sdkError && "Loading secure payment form…"}
        {sdkError && <span style={{ color: "#9B4444" }}>{sdkError}</span>}
      </div>
      {error && <div className="gg-error">{error}</div>}
      {sdkReady && (
        <div style={{ marginTop: 16 }}>
          <button className="gg-btn" disabled={loading} onClick={handleSubmit}>
            {loading ? "Processing…" : buttonLabel || "Save Card"}
          </button>
        </div>
      )}
    </div>
  );
}
